import { createSlice, createEntityAdapter, PayloadAction } from '@reduxjs/toolkit';
import { ChangelogModel, ChangelogsListFiltersModel, ChangelogsListModel } from '@models/api/changelog';
import { editChangelogEntry, EditChangelogEntryThunk, fetchChangelogsList } from './changelogs.thunk';
import { subMonths } from 'date-fns';

const PAGE_SIZE = 40;

const adapter = createEntityAdapter<ChangelogModel>();

const changelogsFiltersInitialState: ChangelogsListFiltersModel = {
  dateRange: [subMonths(new Date(), 3), new Date()],
  application: null,
  categories: [],
  tags: [],
};

const changelogsInitialState = {
  changelogs: adapter.getInitialState(),
  showFilters: false,
  filters: changelogsFiltersInitialState,
  list: {
    currentPage: 1,
    pageSize: PAGE_SIZE,
    allDataLoaded: false,
    isLoading: false,
    chunks: {} as Record<string, ChangelogModel[]>,
  },
  changelogDetailsId: null as number,
};

export const changelogsSlice = createSlice({
  name: 'changelogs',
  initialState: changelogsInitialState,
  reducers: {
    setShowFilters: (state, action: PayloadAction<boolean>) => {
      state.showFilters = action.payload;
    },
    setChangelogsListFilters: (state, action: PayloadAction<Partial<ChangelogsListFiltersModel>>) => {
      state.filters = { ...state.filters, ...(action.payload ?? {}) };
      state.list = changelogsInitialState.list;
      state.changelogDetailsId = changelogsInitialState.changelogDetailsId;
    },
    resetChangelogsState: (state) => {
      state.filters = changelogsInitialState.filters;
      state.list = changelogsInitialState.list;
      state.changelogDetailsId = changelogsInitialState.changelogDetailsId;
    },
    setChangelogDetailsId: (state, action: { payload: number }) => {
      state.changelogDetailsId = action.payload;
    },
    setChangelogHasPdf: (state, action: PayloadAction<{ id: number; hasPdf: boolean }>) => {
      const { id, hasPdf } = action.payload;
      adapter.updateOne(state.changelogs, {
        id,
        changes: { hasPdf },
      });
      state.list.chunks = Object.fromEntries(
        Object.entries(state.list.chunks ?? {}).map(([page, items]) => [
          page,
          items.map((item) => (item.id === id ? { ...item, hasPdf } : item)),
        ])
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchChangelogsList.pending, (state) => {
      state.list.isLoading = true;
    });
    builder.addCase(fetchChangelogsList.fulfilled, (state, action: PayloadAction<ChangelogsListModel>) => {
      const { currentPage, items } = action.payload;

      state.list.allDataLoaded = items.length < state.list.pageSize;

      if (items.length > 0) {
        state.list.currentPage = currentPage;
        state.list.chunks[currentPage] = items;
        adapter.upsertMany(state.changelogs, items);
      }

      state.list.isLoading = false;
    });
    builder.addCase(fetchChangelogsList.rejected, (state) => {
      state.list.isLoading = false;
    });
    //
    builder.addCase(editChangelogEntry.fulfilled, (state, action: PayloadAction<EditChangelogEntryThunk>) => {
      const { id, data } = action.payload;

      adapter.updateOne(state.changelogs, {
        id: id,
        changes: { ...data },
      });

      state.list.chunks = Object.fromEntries(
        Object.entries(state.list.chunks).map(([page, items]) => [
          page,
          items.map((item) =>
            item.id === id
              ? {
                  ...item,
                  ...data,
                }
              : item
          ),
        ])
      );
    });
  },
});
